/**
 * Phase G+ Phase 1 — Lohn & Zeit Einstellungen (28.05.2026).
 *
 * Single editor for ALL payroll/time config. Tabs map 1:1 to backend
 * sections in /app/backend/routes/payroll_settings.py.
 *
 * Anyone with admin / geschaeftsfuehrung / buchhalter role can edit.
 * Backend enforces RBAC; the page just hides nothing.
 */
import { useEffect, useState } from "react";
import { Save, RotateCcw, Plus, Trash2, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const SECTIONS = [
  { key: "kv_categories",  label: "KV-Kategorien" },
  { key: "work_hour_models", label: "Arbeitszeit-Modelle" },
  { key: "dieten",         label: "Diäten" },
  { key: "overtime",       label: "Überstunden" },
  { key: "holiday",        label: "Feiertag" },
  { key: "calendar",       label: "Kalender" },
  { key: "break_rules",    label: "Pausen" },
  { key: "auto_kontrolle", label: "Auto-Kontrolle" },
  { key: "vacation",       label: "Urlaub" },
  { key: "advances",       label: "Akonto" },
  { key: "wage_types",     label: "Lohnarten" },
];

function Hint({ children }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-zinc-800 bg-zinc-900/50 p-3 text-xs text-zinc-400">
      <Info className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" />
      <div>{children}</div>
    </div>
  );
}

export default function PayrollSettings() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState("kv_categories");
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const r = await api.get("/payroll-settings");
      setData(r.data);
    } catch (e) {
      toast.error("Fehler beim Laden der Lohn-Einstellungen");
    }
  }
  useEffect(() => { load(); }, []);

  async function saveSection(section, payload) {
    setSaving(true);
    try {
      const r = await api.put(`/payroll-settings/${section}`, payload);
      setData(r.data);
      toast.success("Gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  }

  async function resetSection(section) {
    if (!window.confirm(`Sektion „${section}" auf Werks­einstellungen zurücksetzen?`.replace(/"/g, "\u201E"))) return;
    try {
      const r = await api.post(`/payroll-settings/reset?section=${section}`);
      setData(r.data);
      toast.success("Auf Defaults zurückgesetzt");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Reset");
    }
  }

  if (!data) return <div className="p-6 text-zinc-500">Lade Einstellungen…</div>;

  const sections = data.sections || {};
  const ctx = { sections, saveSection, resetSection, saving };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="payroll-settings-page">
      <div className="mb-6">
        <h1 className="text-2xl font-display font-bold text-zinc-50">Lohn &amp; Zeit Einstellungen</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Zentrale Konfiguration für Lohnabrechnung, Zeiterfassung, Diäten, Urlaub und Akonto.
          Änderungen wirken sich auf alle künftigen Lohnläufe aus.
        </p>
      </div>

      <Tabs value={active} onValueChange={setActive}>
        <TabsList className="flex flex-wrap gap-1 h-auto bg-zinc-900 p-1">
          {SECTIONS.map((s) => (
            <TabsTrigger key={s.key} value={s.key} data-testid={`tab-${s.key}`} className="text-xs">
              {s.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="kv_categories" className="mt-4"><KVCategories {...ctx} /></TabsContent>
        <TabsContent value="work_hour_models" className="mt-4"><WorkHourModels {...ctx} /></TabsContent>
        <TabsContent value="dieten" className="mt-4"><DietenSettings {...ctx} /></TabsContent>
        <TabsContent value="overtime" className="mt-4"><OvertimeSettings {...ctx} /></TabsContent>
        <TabsContent value="holiday" className="mt-4"><HolidaySettings {...ctx} /></TabsContent>
        <TabsContent value="calendar" className="mt-4"><CalendarSettings {...ctx} /></TabsContent>
        <TabsContent value="break_rules" className="mt-4"><BreakRulesEditor {...ctx} /></TabsContent>
        <TabsContent value="auto_kontrolle" className="mt-4"><AutoKontrolleSettings {...ctx} /></TabsContent>
        <TabsContent value="vacation" className="mt-4"><VacationSettings {...ctx} /></TabsContent>
        <TabsContent value="advances" className="mt-4"><AdvancesSettings {...ctx} /></TabsContent>
        <TabsContent value="wage_types" className="mt-4"><WageTypesEditor {...ctx} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// KV-Kategorien
// ─────────────────────────────────────────────────────────────────────
function KVCategories({ sections, saveSection, resetSection, saving }) {
  const [rows, setRows] = useState(sections.kv_categories || []);
  return (
    <div className="space-y-4">
      <Hint>
        KV Zusteller-Kategorien A–E. Der Brutto-Monatslohn fließt direkt in die Lohnabrechnung jedes
        Fahrers ein, sobald ihm im Profil eine Kategorie zugewiesen wurde.
      </Hint>
      <div className="rounded-lg border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-[10px] uppercase text-zinc-500">
            <tr><th className="px-3 py-2 text-left">Key</th><th className="px-3 py-2 text-left">Bezeichnung</th><th className="px-3 py-2 text-right">Brutto/Monat (€)</th><th className="px-3 py-2 text-left">Notiz</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-zinc-800" data-testid={`kv-row-${r.key}`}>
                <td className="px-3 py-2 font-mono">{r.key}</td>
                <td className="px-3 py-2">
                  <Input value={r.label} onChange={(e) => { const c = [...rows]; c[i] = { ...c[i], label: e.target.value }; setRows(c); }} />
                </td>
                <td className="px-3 py-2">
                  <Input type="number" step="0.01" className="text-right font-mono"
                    value={r.monthly_brutto}
                    onChange={(e) => { const c = [...rows]; c[i] = { ...c[i], monthly_brutto: parseFloat(e.target.value) || 0 }; setRows(c); }} />
                </td>
                <td className="px-3 py-2">
                  <Input value={r.note || ""} onChange={(e) => { const c = [...rows]; c[i] = { ...c[i], note: e.target.value }; setRows(c); }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => resetSection("kv_categories")}><RotateCcw className="w-4 h-4 mr-1.5" />Reset</Button>
        <Button disabled={saving} onClick={() => saveSection("kv_categories", rows)} data-testid="save-kv-categories"><Save className="w-4 h-4 mr-1.5" />Speichern</Button>
      </div>
    </div>
  );
}

function WorkHourModels({ sections, saveSection, resetSection, saving }) {
  const [rows, setRows] = useState(sections.work_hour_models || []);
  return (
    <div className="space-y-4">
      <Hint>5 Wochenarbeitstage als Standard. Samstag = Überstunden. Brutto-Monatslohn pro Modell editierbar.</Hint>
      <div className="rounded-lg border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-[10px] uppercase text-zinc-500">
            <tr><th className="px-3 py-2 text-left">Key</th><th className="px-3 py-2 text-left">Bezeichnung</th><th className="px-3 py-2 text-right">h/Woche</th><th className="px-3 py-2 text-right">Tage/Woche</th><th className="px-3 py-2 text-right">h/Tag</th><th className="px-3 py-2 text-right">Brutto/Monat (€)</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-zinc-800">
                <td className="px-3 py-2 font-mono">{r.key}</td>
                <td className="px-3 py-2"><Input value={r.label} onChange={(e) => { const c = [...rows]; c[i] = { ...c[i], label: e.target.value }; setRows(c); }} /></td>
                <td className="px-3 py-2"><Input type="number" className="text-right" value={r.hours_per_week} onChange={(e) => { const c = [...rows]; c[i] = { ...c[i], hours_per_week: parseFloat(e.target.value) || 0 }; setRows(c); }} /></td>
                <td className="px-3 py-2"><Input type="number" className="text-right" value={r.days_per_week} onChange={(e) => { const c = [...rows]; c[i] = { ...c[i], days_per_week: parseFloat(e.target.value) || 0 }; setRows(c); }} /></td>
                <td className="px-3 py-2"><Input type="number" step="0.5" className="text-right" value={r.hours_per_day} onChange={(e) => { const c = [...rows]; c[i] = { ...c[i], hours_per_day: parseFloat(e.target.value) || 0 }; setRows(c); }} /></td>
                <td className="px-3 py-2"><Input type="number" step="0.01" className="text-right font-mono" value={r.monthly_brutto} onChange={(e) => { const c = [...rows]; c[i] = { ...c[i], monthly_brutto: parseFloat(e.target.value) || 0 }; setRows(c); }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => resetSection("work_hour_models")}><RotateCcw className="w-4 h-4 mr-1.5" />Reset</Button>
        <Button disabled={saving} onClick={() => saveSection("work_hour_models", rows)}><Save className="w-4 h-4 mr-1.5" />Speichern</Button>
      </div>
    </div>
  );
}

function DietenSettings({ sections, saveSection, resetSection, saving }) {
  const [s, setS] = useState(sections.dieten || {});
  return (
    <div className="space-y-4 max-w-3xl">
      <Hint>
        Diätenstunden werden auf die nächste volle Stunde aufgerundet, sobald 1 Minute überschritten ist
        (z.B. 8h 1min = 9 Diätenstunden). Urlaub, Krank, Feiertag &amp; Sonderurlaub erhalten keine Diäten.
      </Hint>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Inland €/Stunde"><Input type="number" step="0.01" value={s.inland_per_hour} onChange={(e) => setS({ ...s, inland_per_hour: parseFloat(e.target.value) || 0 })} data-testid="dieten-inland-hour" /></Field>
        <Field label="Inland €/Tag (≥4h)"><Input type="number" step="0.01" value={s.inland_per_day} onChange={(e) => setS({ ...s, inland_per_day: parseFloat(e.target.value) || 0 })} /></Field>
        <Field label="Ausland €/Tag"><Input type="number" step="0.01" value={s.ausland_per_day} onChange={(e) => setS({ ...s, ausland_per_day: parseFloat(e.target.value) || 0 })} /></Field>
        <Field label="Aufrundungs-Regel">
          <select className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" value={s.rounding_rule} onChange={(e) => setS({ ...s, rounding_rule: e.target.value })}>
            <option value="up_from_first_minute">ab 1. Minute aufrunden</option>
            <option value="up_from_30">ab 30. Minute aufrunden</option>
            <option value="no_rounding">keine Rundung</option>
          </select>
        </Field>
      </div>
      <Field label="Hinweis-Text (intern)"><Input value={s.notes || ""} onChange={(e) => setS({ ...s, notes: e.target.value })} /></Field>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => resetSection("dieten")}><RotateCcw className="w-4 h-4 mr-1.5" />Reset</Button>
        <Button disabled={saving} onClick={() => saveSection("dieten", s)}><Save className="w-4 h-4 mr-1.5" />Speichern</Button>
      </div>
    </div>
  );
}

function OvertimeSettings({ sections, saveSection, resetSection, saving }) {
  const [s, setS] = useState(sections.overtime || {});
  return (
    <div className="space-y-4 max-w-3xl">
      <Hint>Mehrstunden = über Soll-Tag aber unter 40h/Woche. Überstunden = über 40h/Woche. Samstag = 50% OT. Sonntag/Nacht = 100% OT.</Hint>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Mehrstunden %"><Input type="number" step="0.5" value={s.mehrstunden_pct} onChange={(e) => setS({ ...s, mehrstunden_pct: parseFloat(e.target.value) || 0 })} /></Field>
        <Field label="Überstunden 50 %"><Input type="number" step="0.5" value={s.ueberstunden_50_pct} onChange={(e) => setS({ ...s, ueberstunden_50_pct: parseFloat(e.target.value) || 0 })} /></Field>
        <Field label="Überstunden 100 %"><Input type="number" step="0.5" value={s.ueberstunden_100_pct} onChange={(e) => setS({ ...s, ueberstunden_100_pct: parseFloat(e.target.value) || 0 })} /></Field>
      </div>
      <div className="flex items-center gap-4">
        <Toggle label="Samstag = 50 % OT" v={s.saturday_counts_as_ot_50} on={() => setS({ ...s, saturday_counts_as_ot_50: !s.saturday_counts_as_ot_50 })} />
        <Toggle label="Sonntag = 100 % OT" v={s.sunday_counts_as_ot_100} on={() => setS({ ...s, sunday_counts_as_ot_100: !s.sunday_counts_as_ot_100 })} />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => resetSection("overtime")}><RotateCcw className="w-4 h-4 mr-1.5" />Reset</Button>
        <Button disabled={saving} onClick={() => saveSection("overtime", s)}><Save className="w-4 h-4 mr-1.5" />Speichern</Button>
      </div>
    </div>
  );
}

function HolidaySettings({ sections, saveSection, resetSection, saving }) {
  const [s, setS] = useState(sections.holiday || {});
  return (
    <div className="space-y-4 max-w-2xl">
      <Hint>Feiertagsentgelt: Multiplikator des Tagesentgelts wenn frei. Zusätzlicher Multiplikator bei Arbeit am Feiertag.</Hint>
      <Field label="Feiertagsentgelt-Faktor (ohne Arbeit)"><Input type="number" step="0.01" value={s.pay_factor} onChange={(e) => setS({ ...s, pay_factor: parseFloat(e.target.value) || 0 })} /></Field>
      <Field label="Zusätzlicher Faktor bei Arbeit am Feiertag"><Input type="number" step="0.01" value={s.extra_pay_factor_if_worked} onChange={(e) => setS({ ...s, extra_pay_factor_if_worked: parseFloat(e.target.value) || 0 })} /></Field>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => resetSection("holiday")}><RotateCcw className="w-4 h-4 mr-1.5" />Reset</Button>
        <Button disabled={saving} onClick={() => saveSection("holiday", s)}><Save className="w-4 h-4 mr-1.5" />Speichern</Button>
      </div>
    </div>
  );
}

function CalendarSettings({ sections, saveSection, resetSection, saving }) {
  const [s, setS] = useState(sections.calendar || {});
  const overrides = s.monthly_overrides || {};
  const overrideKeys = Object.keys(overrides).sort();
  const [newMonth, setNewMonth] = useState("");
  const [newDays, setNewDays] = useState("");

  const updateOverride = (key, value) => {
    const next = { ...(s.monthly_overrides || {}) };
    if (value === "" || value == null || isNaN(parseInt(value, 10))) {
      delete next[key];
    } else {
      next[key] = parseInt(value, 10);
    }
    setS({ ...s, monthly_overrides: next });
  };

  const addOverride = () => {
    if (!/^\d{4}-\d{2}$/.test(newMonth)) {
      alert("Bitte Format YYYY-MM (z.B. 2026-07) verwenden");
      return;
    }
    const n = parseInt(newDays, 10);
    if (isNaN(n) || n < 1 || n > 31) {
      alert("Tage müssen zwischen 1 und 31 liegen");
      return;
    }
    updateOverride(newMonth, n);
    setNewMonth("");
    setNewDays("");
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <Hint>Arbeitstage und Feiertage pro Monat. Verwendet für Diäten- und Lohnberechnung.</Hint>
      <Field label="Standard Arbeitstage pro Monat"><Input type="number" value={s.working_days_per_month ?? 22} onChange={(e) => setS({ ...s, working_days_per_month: parseInt(e.target.value) || 0 })} /></Field>
      <Field label="Feiertage pro Monat (Default)"><Input type="number" value={s.default_holidays_per_month ?? 0} onChange={(e) => setS({ ...s, default_holidays_per_month: parseInt(e.target.value) || 0 })} /></Field>

      {/* Phase G+ P5b — Monats-Overrides */}
      <div className="border-t border-zinc-800 pt-4 space-y-2">
        <div className="text-sm font-semibold text-zinc-100">Pro Monat überschreiben</div>
        <Hint>
          Setze hier vor jedem Lohnlauf den exakten Wert für den jeweiligen Monat
          (z.B. {`2026-07`} = 23 Tage). Die Lohnlauf-Vorschau nimmt automatisch
          den Override-Wert für den ausgewählten Monat. Ohne Eintrag gilt der Standard oben.
        </Hint>
        {overrideKeys.length > 0 && (
          <table className="w-full text-xs" data-testid="calendar-overrides-table">
            <thead><tr className="text-zinc-400 text-left">
              <th className="px-2 py-1">Monat</th>
              <th className="px-2 py-1">Arbeitstage</th>
              <th className="w-10"></th>
            </tr></thead>
            <tbody>
              {overrideKeys.map(k => (
                <tr key={k} className="border-t border-zinc-800">
                  <td className="px-2 py-1 text-zinc-100">{k}</td>
                  <td className="px-2 py-1">
                    <Input type="number"
                           value={overrides[k]}
                           onChange={(e) => updateOverride(k, e.target.value)}
                           data-testid={`calendar-override-${k}`}
                           className="w-24"/>
                  </td>
                  <td className="px-2 py-1 text-right">
                    <Button size="sm" variant="ghost"
                            onClick={() => updateOverride(k, "")}
                            data-testid={`calendar-override-del-${k}`}>×</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="flex items-center gap-2 pt-2">
          <Input placeholder="YYYY-MM (z.B. 2026-07)" value={newMonth}
                 onChange={(e) => setNewMonth(e.target.value)}
                 data-testid="calendar-override-new-month" className="w-40"/>
          <Input type="number" placeholder="Arbeitstage" value={newDays}
                 onChange={(e) => setNewDays(e.target.value)}
                 data-testid="calendar-override-new-days" className="w-32"/>
          <Button onClick={addOverride} data-testid="calendar-override-add">
            <Plus className="w-4 h-4 mr-1" /> Override hinzufügen
          </Button>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => resetSection("calendar")}><RotateCcw className="w-4 h-4 mr-1.5" />Reset</Button>
        <Button disabled={saving} onClick={() => saveSection("calendar", s)} data-testid="calendar-save"><Save className="w-4 h-4 mr-1.5" />Speichern</Button>
      </div>
    </div>
  );
}

function BreakRulesEditor({ sections, saveSection, resetSection, saving }) {
  const [rows, setRows] = useState(sections.break_rules || []);
  return (
    <div className="space-y-4">
      <Hint>
        Pausen sind keine bezahlte Arbeitszeit aber zählen für Diäten. Auto-Generator wendet diese
        Regeln pro Arbeitsschicht an. Beispiel: ~8h → 33–37 min; ≥9h → 33–37 + 15–18 min.
      </Hint>
      <div className="rounded-lg border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-[10px] uppercase text-zinc-500">
            <tr><th className="px-3 py-2 text-left">Wenn Arbeit &lt; Min</th><th className="px-3 py-2 text-left">Pausen (min/max)</th><th className="w-10"></th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-zinc-800 align-top">
                <td className="px-3 py-2 w-48"><Input type="number" value={r.if_work_min_lt} onChange={(e) => { const c = [...rows]; c[i] = { ...c[i], if_work_min_lt: parseInt(e.target.value) || 0 }; setRows(c); }} /></td>
                <td className="px-3 py-2">
                  <div className="space-y-1">
                    {(r.breaks || []).map((br, j) => (
                      <div key={j} className="flex items-center gap-2">
                        <Input type="number" className="w-24" value={br.min} onChange={(e) => { const c = [...rows]; c[i].breaks[j] = { ...c[i].breaks[j], min: parseInt(e.target.value) || 0 }; setRows(c); }} placeholder="min" />
                        <span className="text-zinc-500">–</span>
                        <Input type="number" className="w-24" value={br.max} onChange={(e) => { const c = [...rows]; c[i].breaks[j] = { ...c[i].breaks[j], max: parseInt(e.target.value) || 0 }; setRows(c); }} placeholder="max" />
                        <span className="text-xs text-zinc-500">min</span>
                        <Button size="icon" variant="ghost" onClick={() => { const c = [...rows]; c[i].breaks.splice(j, 1); setRows(c); }}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                      </div>
                    ))}
                    <Button size="sm" variant="ghost" onClick={() => { const c = [...rows]; c[i].breaks = [...(c[i].breaks || []), { min: 15, max: 18 }]; setRows(c); }}><Plus className="w-4 h-4 mr-1" />Pause hinzufügen</Button>
                  </div>
                </td>
                <td className="px-3 py-2 text-right"><Button size="icon" variant="ghost" onClick={() => setRows(rows.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4 text-red-400" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button size="sm" variant="ghost" onClick={() => setRows([...rows, { if_work_min_lt: 999, breaks: [] }])}><Plus className="w-4 h-4 mr-1.5" />Regel hinzufügen</Button>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => resetSection("break_rules")}><RotateCcw className="w-4 h-4 mr-1.5" />Reset</Button>
        <Button disabled={saving} onClick={() => saveSection("break_rules", rows)}><Save className="w-4 h-4 mr-1.5" />Speichern</Button>
      </div>
    </div>
  );
}

function AutoKontrolleSettings({ sections, saveSection, resetSection, saving }) {
  const [s, setS] = useState(sections.auto_kontrolle || {});
  const block = (key, title) => {
    const v = s[key] || {};
    return (
      <div className="rounded-lg border border-zinc-800 p-4 space-y-3" data-testid={`auto-kontrolle-${key}`}>
        <h3 className="font-display font-semibold text-zinc-100">{title}</h3>
        <Field label="Bezeichnung"><Input value={v.label || ""} onChange={(e) => setS({ ...s, [key]: { ...v, label: e.target.value } })} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Min Minuten"><Input type="number" value={v.min_minutes} onChange={(e) => setS({ ...s, [key]: { ...v, min_minutes: parseInt(e.target.value) || 0 } })} /></Field>
          <Field label="Max Minuten"><Input type="number" value={v.max_minutes} onChange={(e) => setS({ ...s, [key]: { ...v, max_minutes: parseInt(e.target.value) || 0 } })} /></Field>
        </div>
        <div className="flex gap-4">
          <Toggle label="Bezahlt" v={v.paid} on={() => setS({ ...s, [key]: { ...v, paid: !v.paid } })} />
          <Toggle label="Zählt für Diäten" v={v.counts_for_dieten} on={() => setS({ ...s, [key]: { ...v, counts_for_dieten: !v.counts_for_dieten } })} />
        </div>
      </div>
    );
  };
  return (
    <div className="space-y-4">
      <Hint>
        SAZ1 → „Auto Kontrolle Früh&ldquo; (vor der Tour). SAZ3 → „Auto Kontrolle nach Tour&ldquo;. Beide sind
        bezahlte Arbeitszeit, aber <b>ohne Diäten</b>. Auto-Generator erzeugt diese Blöcke automatisch.
      </Hint>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {block("frueh", "Auto Kontrolle Früh (SAZ1)")}
        {block("nach_tour", "Auto Kontrolle nach Tour (SAZ3)")}
        {block("lager", "SAZ Lager")}
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => resetSection("auto_kontrolle")}><RotateCcw className="w-4 h-4 mr-1.5" />Reset</Button>
        <Button disabled={saving} onClick={() => saveSection("auto_kontrolle", s)}><Save className="w-4 h-4 mr-1.5" />Speichern</Button>
      </div>
    </div>
  );
}

function VacationSettings({ sections, saveSection, resetSection, saving }) {
  const [s, setS] = useState(sections.vacation || {});
  return (
    <div className="space-y-4 max-w-2xl">
      <Hint>Am Dienst-Jubiläum erhält jeder Fahrer neuen Anspruch. Altanspruch und neuer Anspruch werden getrennt geführt für Offboarding.</Hint>
      <Field label="Jahresanspruch (Tage)"><Input type="number" value={s.default_entitlement_days_per_year} onChange={(e) => setS({ ...s, default_entitlement_days_per_year: parseInt(e.target.value) || 0 })} /></Field>
      <Toggle label="Jubiläum vergibt neuen 25-Tage-Anspruch" v={s.anniversary_grants_new_25} on={() => setS({ ...s, anniversary_grants_new_25: !s.anniversary_grants_new_25 })} />
      <Field label="Urlaubsgeld-Faktor pro Tag"><Input type="number" step="0.01" value={s.vacation_pay_factor_per_day} onChange={(e) => setS({ ...s, vacation_pay_factor_per_day: parseFloat(e.target.value) || 0 })} /></Field>
      <Field label="Übertrag verfällt nach (Monate)"><Input type="number" value={s.carry_over_expires_after_months} onChange={(e) => setS({ ...s, carry_over_expires_after_months: parseInt(e.target.value) || 0 })} /></Field>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => resetSection("vacation")}><RotateCcw className="w-4 h-4 mr-1.5" />Reset</Button>
        <Button disabled={saving} onClick={() => saveSection("vacation", s)}><Save className="w-4 h-4 mr-1.5" />Speichern</Button>
      </div>
    </div>
  );
}

function AdvancesSettings({ sections, saveSection, resetSection, saving }) {
  const [s, setS] = useState(sections.advances || {});
  const reasons = s.default_reasons || [];
  return (
    <div className="space-y-4 max-w-2xl">
      <Hint>Vorschüsse / Akonto. Max. Raten pro Vorschuss + Standard-Gründe für das Anlage-Formular.</Hint>
      <Field label="Maximale Raten"><Input type="number" value={s.max_installments} onChange={(e) => setS({ ...s, max_installments: parseInt(e.target.value) || 0 })} /></Field>
      <Toggle label="Automatisch in Lohnabrechnung abziehen" v={s.auto_deduct_in_payroll} on={() => setS({ ...s, auto_deduct_in_payroll: !s.auto_deduct_in_payroll })} />
      <div>
        <Label className="text-zinc-300 text-xs">Standard-Gründe (eine pro Zeile)</Label>
        <textarea className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded p-2 text-sm font-mono" rows={6}
          value={reasons.join("\n")} onChange={(e) => setS({ ...s, default_reasons: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => resetSection("advances")}><RotateCcw className="w-4 h-4 mr-1.5" />Reset</Button>
        <Button disabled={saving} onClick={() => saveSection("advances", s)}><Save className="w-4 h-4 mr-1.5" />Speichern</Button>
      </div>
    </div>
  );
}

function WageTypesEditor({ sections, saveSection, resetSection, saving }) {
  const [rows, setRows] = useState(sections.wage_types || []);
  return (
    <div className="space-y-4">
      <Hint>Lohnarten für Export an Lohnverrechner. Codes müssen mit dem ERP des Steuerberaters übereinstimmen.</Hint>
      <div className="rounded-lg border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-[10px] uppercase text-zinc-500">
            <tr><th className="px-3 py-2 text-left">Code</th><th className="px-3 py-2 text-left">Bezeichnung</th><th className="px-3 py-2 text-left">Aktiv</th><th className="w-10"></th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-zinc-800">
                <td className="px-3 py-2"><Input className="font-mono w-24" value={r.code} onChange={(e) => { const c = [...rows]; c[i] = { ...c[i], code: e.target.value }; setRows(c); }} /></td>
                <td className="px-3 py-2"><Input value={r.label} onChange={(e) => { const c = [...rows]; c[i] = { ...c[i], label: e.target.value }; setRows(c); }} /></td>
                <td className="px-3 py-2"><Switch checked={r.active !== false} onCheckedChange={(v) => { const c = [...rows]; c[i] = { ...c[i], active: v }; setRows(c); }} /></td>
                <td className="px-3 py-2 text-right"><Button size="icon" variant="ghost" onClick={() => setRows(rows.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4 text-red-400" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button size="sm" variant="ghost" onClick={() => setRows([...rows, { code: "", label: "", active: true }])}><Plus className="w-4 h-4 mr-1.5" />Lohnart hinzufügen</Button>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => resetSection("wage_types")}><RotateCcw className="w-4 h-4 mr-1.5" />Reset</Button>
        <Button disabled={saving} onClick={() => saveSection("wage_types", rows)}><Save className="w-4 h-4 mr-1.5" />Speichern</Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Small helpers
// ─────────────────────────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-zinc-300 text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Toggle({ label, v, on }) {
  return (
    <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
      <Switch checked={!!v} onCheckedChange={on} />
      {label}
    </label>
  );
}
